import { AuthHeader } from "@/features/auth/components/AuthHeader";
import { AuthHeaderSubtitle } from "@/features/auth/components/AuthHeaderSubtitle";
import { AuthHeaderTitle } from "@/features/auth/components/AuthHeaderTitle";
import { useFocusOnLoad } from "@/lib/common/useFocus";
import { useTitle } from "@/lib/common/useTitle";
import { z } from "zod";
import { useMemo, type FC } from "react";
import { useAppForm } from "@/lib/form/useAppForm";
import { Form } from "@/lib/form/components/Form";
import { FieldDescription } from "@/ds/shadcn/components/ui/field";
import { Link } from "react-router";
import { useInitiateForgotPassword } from "@/features/auth/api/useInitiateForgotPassword";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { Alert, AlertDescription } from "@/ds/shadcn/components/ui/alert";
import { CheckCircle2Icon } from "lucide-react";

export const PageInitiateForgotPassword: FC = () => {
	useTitle("Forgot Password");

	const ref = useFocusOnLoad();
	const initiateForgotPassword = useInitiateForgotPassword();

	const validators = useMemo(() => z.object({
		emailAddress: z.email("Please supply a valid email address")
	}), []);

	const form = useAppForm({
		defaultValues: {
			emailAddress: ""
		},
		validators: {
			onMount: validators,
			onChange: validators
		},
		onSubmit: async ({ value }) => initiateForgotPassword.mutateAsync({
			emailAddress: value.emailAddress
		})
	});

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit();
	};

	return (
		<>
			<AuthHeader>
				<AuthHeaderTitle>Forgot Password</AuthHeaderTitle>
				<AuthHeaderSubtitle>Please provide your email address, we will send you a password reset email</AuthHeaderSubtitle>
			</AuthHeader>

			{
				!initiateForgotPassword.isSuccess &&
				<Form onSubmit={onSubmit}>
					<form.AppField name="emailAddress">
						{
							field => <field.TextField label="Email Address" type="email" inputMode="email" ref={ref} required />
						}
					</form.AppField>

					<ErrorDisplay error={initiateForgotPassword.error} />

					<form.AppForm>
						<form.SubmitButton label="Confirm" />
					</form.AppForm>
					<FieldDescription className="text-center">
						Remembered your password? <Link to="../.." className="underline underline-offset-4">Sign in</Link>
					</FieldDescription>
				</Form>
			}

			{
				initiateForgotPassword.isSuccess &&
				<Alert>
					<CheckCircle2Icon />
					<AlertDescription>
						We've just sent you an email with instructions on how to reset your password.  If you cannot find the email, you may need to check your spam folder.
					</AlertDescription>
				</Alert>
			}
		</>
	);
};
